import { useState } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Search,
  Loader2,
  MapPin,
  DollarSign,
  Home,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Filter,
  Target,
  Hammer,
  PiggyBank,
  Building2,
  BedDouble,
  Bath,
  Square,
  Clock,
  ChevronDown,
  ChevronUp,
  Sparkles
} from "lucide-react";

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming"
];

const PROPERTY_TYPES = [
  { id: "single_family", label: "Single Family" },
  { id: "multi_family", label: "Multi-Family" },
  { id: "condo", label: "Condo" },
  { id: "townhouse", label: "Townhouse" }
];

export default function DealFinder() {
  const { api } = useAuth();
  
  // Search state
  const [state, setState] = useState("");
  const [cities, setCities] = useState("");
  const [minBedrooms, setMinBedrooms] = useState("");
  const [minBathrooms, setMinBathrooms] = useState("");
  const [maxDaysOnMarket, setMaxDaysOnMarket] = useState("");
  const [minProfitMargin, setMinProfitMargin] = useState("30");
  const [propertyTypes, setPropertyTypes] = useState(["single_family", "multi_family", "condo", "townhouse"]);
  
  // Results state
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [expandedDeal, setExpandedDeal] = useState(null);
  const [showFilters, setShowFilters] = useState(true);

  const togglePropertyType = (typeId) => {
    setPropertyTypes(prev => 
      prev.includes(typeId) 
        ? prev.filter(t => t !== typeId)
        : [...prev, typeId]
    );
  };

  const searchDeals = async () => {
    if (!state) {
      toast.error("Please select a state");
      return;
    }
    
    setLoading(true);
    setResults(null);
    toast.info("🔍 Searching MLS listings & analyzing deals...", { duration: 10000 });
    
    try {
      const searchParams = {
        state: state,
        property_types: propertyTypes,
        min_profit_margin: parseFloat(minProfitMargin) || 30
      };
      
      if (cities.trim()) {
        searchParams.cities = cities.split(",").map(c => c.trim()).filter(c => c);
      }
      if (minBedrooms && minBedrooms !== "any") {
        searchParams.min_bedrooms = parseInt(minBedrooms);
      }
      if (minBathrooms && minBathrooms !== "any") {
        searchParams.min_bathrooms = parseFloat(minBathrooms);
      }
      if (maxDaysOnMarket) {
        searchParams.max_days_on_market = parseInt(maxDaysOnMarket);
      }
      
      const res = await api.post("/ai/deal-finder/search", searchParams);
      setResults(res.data);
      
      if (res.data.total_qualified > 0) {
        toast.success(`Found ${res.data.total_qualified} deals with ${minProfitMargin}%+ profit margin!`);
      } else if (res.data.error_type === "data_unavailable") {
        toast.error(res.data.message || "Unable to fetch listings. Please try again later.");
      } else if (res.data.message) {
        toast.warning(res.data.message);
      } else {
        toast.warning("No deals found matching criteria. Try adjusting filters.");
      }
    } catch (e) {
      const errorMsg = e.response?.data?.detail || "Search failed. Please try again.";
      if (errorMsg.includes("Budget")) {
        toast.error("AI service budget exceeded. Please contact support or try again later.");
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const getDealScoreColor = (score) => {
    if (score >= 8) return "text-green-400 bg-green-500/20";
    if (score >= 6) return "text-yellow-400 bg-yellow-500/20";
    return "text-red-400 bg-red-500/20";
  };

  const getRiskColor = (risk) => {
    if (risk === "low") return "text-green-400";
    if (risk === "medium") return "text-yellow-400";
    return "text-red-400";
  };

  const formatCurrency = (value) => {
    if (!value) return "N/A";
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3" style={{ fontFamily: 'Manrope' }}>
              <Target className="w-8 h-8 text-emerald-400" />
              AI Deal Finder
            </h1>
            <p className="text-zinc-400 mt-1">Find flip opportunities with 30%+ profit margin</p>
          </div>
          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
            <Sparkles className="w-3 h-3 mr-1" />
            AI-Powered
          </Badge>
        </div>

        {/* Search Panel */}
        <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
          <CardHeader className="cursor-pointer" onClick={() => setShowFilters(!showFilters)}>
            <CardTitle className="text-lg text-white flex items-center justify-between" style={{ fontFamily: 'Manrope' }}>
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-emerald-400" />
                Search Filters
              </div>
              {showFilters ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
            </CardTitle>
          </CardHeader>
          
          {showFilters && (
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* State Selection */}
                <div className="space-y-2">
                  <Label className="text-zinc-300">State *</Label>
                  <Select value={state} onValueChange={setState}>
                    <SelectTrigger className="bg-black/50 border-white/10 rounded-sm">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10 max-h-60">
                      {US_STATES.map(s => (
                        <SelectItem key={s} value={s} className="text-zinc-200">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Cities */}
                <div className="space-y-2">
                  <Label className="text-zinc-300">Cities (optional)</Label>
                  <Input
                    value={cities}
                    onChange={(e) => setCities(e.target.value)}
                    placeholder="Austin, Dallas, Houston"
                    className="bg-black/50 border-white/10 rounded-sm"
                  />
                  <p className="text-xs text-zinc-500">Comma-separated</p>
                </div>

                {/* Min Bedrooms */}
                <div className="space-y-2">
                  <Label className="text-zinc-300">Min Bedrooms</Label>
                  <Select value={minBedrooms} onValueChange={setMinBedrooms}>
                    <SelectTrigger className="bg-black/50 border-white/10 rounded-sm">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      <SelectItem value="any" className="text-zinc-200">Any</SelectItem>
                      {[1, 2, 3, 4, 5].map(n => (
                        <SelectItem key={n} value={n.toString()} className="text-zinc-200">{n}+</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Min Bathrooms */}
                <div className="space-y-2">
                  <Label className="text-zinc-300">Min Bathrooms</Label>
                  <Select value={minBathrooms} onValueChange={setMinBathrooms}>
                    <SelectTrigger className="bg-black/50 border-white/10 rounded-sm">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      <SelectItem value="any" className="text-zinc-200">Any</SelectItem>
                      {[1, 1.5, 2, 2.5, 3, 4].map(n => (
                        <SelectItem key={n} value={n.toString()} className="text-zinc-200">{n}+</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Min Profit Margin */}
                <div className="space-y-2">
                  <Label className="text-zinc-300">Minimum Profit Margin</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={minProfitMargin}
                      onChange={(e) => setMinProfitMargin(e.target.value)}
                      placeholder="30"
                      className="bg-black/50 border-white/10 rounded-sm w-24"
                    />
                    <span className="text-zinc-400">%</span>
                  </div>
                </div>

                {/* Max Days on Market */}
                <div className="space-y-2">
                  <Label className="text-zinc-300">Max Days on Market</Label>
                  <Input
                    type="number"
                    value={maxDaysOnMarket}
                    onChange={(e) => setMaxDaysOnMarket(e.target.value)}
                    placeholder="Any (stale = motivated)"
                    className="bg-black/50 border-white/10 rounded-sm"
                  />
                </div>
              </div>

              {/* Property Types */}
              <div className="space-y-2">
                <Label className="text-zinc-300">Property Types</Label>
                <div className="flex flex-wrap gap-3">
                  {PROPERTY_TYPES.map(type => (
                    <div key={type.id} className="flex items-center gap-2">
                      <Checkbox
                        id={type.id}
                        checked={propertyTypes.includes(type.id)}
                        onCheckedChange={() => togglePropertyType(type.id)}
                        className="border-white/20"
                      />
                      <label htmlFor={type.id} className="text-sm text-zinc-300 cursor-pointer">
                        {type.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Search Button */}
              <Button
                onClick={searchDeals}
                disabled={loading || !state}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-12"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Searching & Analyzing Deals...
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5 mr-2" />
                    Find Flip Deals in {state || "..."}
                  </>
                )}
              </Button>
              
              {loading && (
                <div className="text-center text-sm text-zinc-500">
                  <p>🔍 Searching MLS listings across {state}...</p>
                  <p>🤖 AI analyzing each property for flip potential...</p>
                  <p>This may take 30-60 seconds</p>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Results */}
        {results && (
          <div className="space-y-4">
            {/* Results Summary */}
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardContent className="py-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-white">{results.total_searched}</p>
                      <p className="text-xs text-zinc-500">Listings Found</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-yellow-400">{results.total_filtered}</p>
                      <p className="text-xs text-zinc-500">After Filters</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-emerald-400">{results.total_qualified}</p>
                      <p className="text-xs text-zinc-500">{minProfitMargin}%+ Deals</p>
                    </div>
                  </div>
                  <Badge className="bg-zinc-800 text-zinc-300">
                    Searched: {results.search_criteria?.state}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Deals List */}
            {results.deals && results.deals.length > 0 ? (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  Qualified Deals ({results.deals.length})
                </h2>
                
                {results.deals.map((deal, index) => (
                  <Card 
                    key={index} 
                    className={`bg-[#0A0A0A] border-white/10 rounded-none transition-all ${
                      expandedDeal === index ? 'border-emerald-500/50' : 'hover:border-white/20'
                    }`}
                  >
                    <CardContent className="p-4">
                      {/* Deal Header */}
                      <div 
                        className="flex items-start justify-between cursor-pointer"
                        onClick={() => setExpandedDeal(expandedDeal === index ? null : index)}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`px-2 py-1 rounded text-sm font-bold ${getDealScoreColor(deal.analysis?.deal_score || 0)}`}>
                              {deal.analysis?.deal_score || "?"}/10
                            </span>
                            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                              {deal.analysis?.profit_margin_pct?.toFixed(1) || "?"}% ROI
                            </Badge>
                            <Badge className={`${getRiskColor(deal.analysis?.risk_level)} bg-transparent border-current`}>
                              {deal.analysis?.risk_level || "?"} risk
                            </Badge>
                          </div>
                          
                          <h3 className="text-white font-medium text-lg mb-1">{deal.title}</h3>
                          
                          <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-400">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {deal.state}
                            </span>
                            {deal.price && (
                              <span className="flex items-center gap-1 text-emerald-400">
                                <DollarSign className="w-4 h-4" />
                                {formatCurrency(deal.price)}
                              </span>
                            )}
                            {deal.bedrooms && (
                              <span className="flex items-center gap-1">
                                <BedDouble className="w-4 h-4" />
                                {deal.bedrooms} bed
                              </span>
                            )}
                            {deal.bathrooms && (
                              <span className="flex items-center gap-1">
                                <Bath className="w-4 h-4" />
                                {deal.bathrooms} bath
                              </span>
                            )}
                            {deal.sqft && (
                              <span className="flex items-center gap-1">
                                <Square className="w-4 h-4" />
                                {deal.sqft.toLocaleString()} sqft
                              </span>
                            )}
                            {/* Source Badge - ALWAYS VISIBLE */}
                            {deal.source && (
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                deal.is_ai_generated ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' :
                                deal.source === 'Zillow' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                                deal.source === 'Realtor.com' ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                                deal.source === 'Trulia' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                                'bg-zinc-500/20 text-zinc-400'
                              }`}>
                                {deal.is_ai_generated ? '🤖 AI Generated' : deal.source}
                              </span>
                            )}
                            {/* Active Badge */}
                            {deal.listing_status === 'active' && !deal.is_ai_generated && (
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                                ✓ Active Listing
                              </span>
                            )}
                          </div>
                          
                          {/* VIEW ON ZILLOW/TRULIA BUTTON - ALWAYS VISIBLE */}
                          {deal.url && (
                            <div className="mt-3">
                              <a 
                                href={deal.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className={`inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-colors ${
                                  deal.is_ai_generated ? 'bg-purple-600 hover:bg-purple-700 text-white' :
                                  deal.source === 'Zillow' ? 'bg-blue-600 hover:bg-blue-700 text-white' :
                                  deal.source === 'Realtor.com' ? 'bg-red-600 hover:bg-red-700 text-white' :
                                  deal.source === 'Trulia' ? 'bg-green-600 hover:bg-green-700 text-white' :
                                  'bg-zinc-600 hover:bg-zinc-700 text-white'
                                }`}
                                data-testid={`view-listing-${index}`}
                              >
                                <ExternalLink className="w-4 h-4" />
                                {deal.is_ai_generated ? 'Search on Zillow' : `View on ${deal.source || 'Website'}`}
                              </a>
                            </div>
                          )}
                          {/* Fallback: Create search link if no URL */}
                          {!deal.url && deal.title && (
                            <div className="mt-3">
                              <a 
                                href={`https://www.zillow.com/homes/${encodeURIComponent(deal.title)}_rb/`}
                                target="_blank" 
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                                data-testid={`search-listing-${index}`}
                              >
                                <Search className="w-4 h-4" />
                                Search on Zillow
                              </a>
                            </div>
                          )}
                        </div>
                        
                        <div className="text-right">
                          <p className="text-emerald-400 font-bold text-xl">
                            {formatCurrency(deal.analysis?.estimated_profit)}
                          </p>
                          <p className="text-xs text-zinc-500">Est. Profit</p>
                          {expandedDeal === index ? (
                            <ChevronUp className="w-5 h-5 text-zinc-400 mt-2 ml-auto" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-zinc-400 mt-2 ml-auto" />
                          )}
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {expandedDeal === index && deal.analysis && (
                        <div className="mt-4 pt-4 border-t border-white/10 space-y-4">
                          {/* Quick Summary */}
                          <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded">
                            <p className="text-sm text-emerald-300">{deal.analysis.why_good_deal}</p>
                          </div>
                          
                          {/* Financial Breakdown */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="p-3 bg-black/30 rounded">
                              <p className="text-xs text-zinc-500 mb-1">Purchase Price</p>
                              <p className="text-lg font-semibold text-white">{formatCurrency(deal.analysis.estimated_purchase || deal.price)}</p>
                            </div>
                            <div className="p-3 bg-black/30 rounded">
                              <p className="text-xs text-zinc-500 mb-1">Repair Costs</p>
                              <p className="text-lg font-semibold text-yellow-400">{formatCurrency(deal.analysis.estimated_repairs)}</p>
                            </div>
                            <div className="p-3 bg-black/30 rounded">
                              <p className="text-xs text-zinc-500 mb-1">ARV</p>
                              <p className="text-lg font-semibold text-blue-400">{formatCurrency(deal.analysis.estimated_arv)}</p>
                            </div>
                            <div className="p-3 bg-black/30 rounded">
                              <p className="text-xs text-zinc-500 mb-1">Total Investment</p>
                              <p className="text-lg font-semibold text-white">{formatCurrency(deal.analysis.total_investment)}</p>
                            </div>
                          </div>

                          {/* Repair Breakdown */}
                          {deal.analysis.repair_breakdown && (
                            <div className="p-3 bg-black/30 rounded">
                              <p className="text-sm font-medium text-zinc-300 mb-2 flex items-center gap-2">
                                <Hammer className="w-4 h-4 text-orange-400" />
                                Repair Cost Breakdown
                              </p>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                                {Object.entries(deal.analysis.repair_breakdown).map(([key, value]) => (
                                  <div key={key} className="flex justify-between">
                                    <span className="text-zinc-500 capitalize">{key}:</span>
                                    <span className="text-zinc-300">{formatCurrency(value)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Key Risks */}
                          {deal.analysis.key_risks && (
                            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded">
                              <p className="text-sm font-medium text-red-400 mb-1 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4" />
                                Key Risks
                              </p>
                              <p className="text-sm text-zinc-400">{deal.analysis.key_risks}</p>
                            </div>
                          )}

                          {/* Recommended Offer */}
                          <div className="flex items-center justify-between p-3 bg-emerald-500/10 border border-emerald-500/30 rounded">
                            <div>
                              <p className="text-sm text-zinc-400">Recommended Max Offer for 30% ROI</p>
                              <p className="text-2xl font-bold text-emerald-400">{formatCurrency(deal.analysis.recommended_offer)}</p>
                            </div>
                            {deal.url ? (
                              <a 
                                href={deal.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className={`flex items-center gap-2 px-4 py-2 rounded text-sm ${
                                  deal.is_ai_generated ? 'bg-purple-600 hover:bg-purple-700' : 'bg-emerald-600 hover:bg-emerald-700'
                                } text-white`}
                              >
                                {deal.is_ai_generated ? 'Search Listing' : 'View Listing'} <ExternalLink className="w-4 h-4" />
                              </a>
                            ) : deal.title && (
                              <a 
                                href={`https://www.zillow.com/homes/${encodeURIComponent(deal.title)}_rb/`}
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
                              >
                                Search on Zillow <ExternalLink className="w-4 h-4" />
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                <CardContent className="py-12 text-center">
                  <AlertTriangle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">No Qualifying Deals Found</h3>
                  <p className="text-zinc-400 max-w-md mx-auto">
                    No properties in {results.search_criteria?.state} met the {minProfitMargin}% profit margin threshold.
                    Try lowering the minimum profit margin or searching a different state.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Empty State */}
        {!loading && !results && (
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="py-16 text-center">
              <Target className="w-16 h-16 text-emerald-400/50 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-white mb-2">Find Your Next Flip Deal</h3>
              <p className="text-zinc-400 max-w-lg mx-auto mb-6">
                Our AI searches MLS listings across your selected state, analyzes each property for flip potential,
                and identifies deals with {minProfitMargin}%+ profit margins.
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-sm text-zinc-500">
                <span className="flex items-center gap-2">
                  <Search className="w-4 h-4 text-emerald-400" />
                  Searches multiple listing sources
                </span>
                <span className="flex items-center gap-2">
                  <Hammer className="w-4 h-4 text-emerald-400" />
                  Estimates repair costs
                </span>
                <span className="flex items-center gap-2">
                  <PiggyBank className="w-4 h-4 text-emerald-400" />
                  Calculates ROI
                </span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
